import React, {type CSSProperties, type MouseEvent, type ReactNode} from "react";
import cn from "classnames";
import {useImages} from "../../contexts/images";
import {nextButtonColor, type ButtonColor} from "../../libs/bannerButton";
import {textColorStyle} from "../../libs/textColor";
import type {ElementColors} from "../../libs/sceneColors";
import {useFeature} from "../../hooks/useFeature";
import "../Tooltip/index.css";
import "./index.css";

type BannerButtonProps = {
  /** The template id. Each banner keeps its own button color. */
  id: string;
  className?: string;
  children: ReactNode;
};

/**
 * The call-to-action pill of a banner. Option+click (Alt+click) toggles its
 * fill between light and dark. The label always has the inverse color of the
 * fill and does not follow the title color (see index.css). A plain click goes
 * to the label, which stays editable.
 */
export default function BannerButton({id, className, children}: BannerButtonProps) {
  const {buttonColors, elementColors, editButtonColor} = useImages() as {
    buttonColors: Record<string, ButtonColor | undefined>;
    elementColors: Record<string, ElementColors>;
    editButtonColor: (id: string, color: ButtonColor) => void;
  };
  const color = buttonColors[id];
  const fill = elementColors[id]?.buttonFill;
  const customStyle = fill ? {"--banner-button-fill": fill,
    "--banner-button-label": textColorStyle(fill)!.dark ? "#fff" : "#000"} as CSSProperties : undefined;
  // Role feature "banner.button-recolor" (data/features.ts).
  const canRecolor = useFeature("banner.button-recolor");

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!event.altKey) return;
    event.preventDefault();
    // The document handler of hooks/useLogoCycling.js recolors the banner text
    // on an Option+click. This click recolors the button only, and without
    // the feature it does nothing.
    event.stopPropagation();
    if (canRecolor) editButtonColor(id, nextButtonColor(color));
  };

  return (
    <div
      className={cn("banner-button", {"instant-tooltip": canRecolor}, className)}
      style={customStyle}
      data-button-color={color}
      data-tooltip={canRecolor ? "Option+click to recolor" : undefined}
      onClick={handleClick}
    >
      {children}
    </div>
  );
}
